import { Box, BoxProps, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { WfHeading } from "src/components/base/wf-heading";
import { WfLink } from "src/components/base/wf-link";
import { ExpandableTags } from "src/components/common/expandable-tags/expandable-tags";
import { IconDpaAfx, IconTrending } from "src/components/icons";
import { useDateTimeFormatter } from "src/hooks/formatters/use-date-time-formatter";
import { NEWS_TOP_SECTION_TEST_ID } from "./news.test-ids";

interface INewsTopSectionProps extends BoxProps {
    headline: string;
    tags: string[];
    creationDate?: Date;
    location?: string;
    numberOfReactions: number;
    numberOfComments: number;
    numberOfLikes: number;
    likeSectionId: string;
    commentSectionId: string;
    reactionSectionId: string;
}

const NEWS_COMMUNITY_REACTIONS_TEST_ID = "news-community-reactions";

export const NewsTopSection = ({
    headline,
    tags,
    creationDate,
    location,
    numberOfReactions,
    numberOfComments,
    numberOfLikes,
    likeSectionId,
    commentSectionId,
    reactionSectionId,
    ...boxProps
}: INewsTopSectionProps) => {
    const { formatDateShort, formatTimeShort } = useDateTimeFormatter();
    const { t } = useTranslation("news");
    const totalInteractions = numberOfComments + numberOfLikes + numberOfReactions;

    return (
        <Box {...boxProps} data-test-id={NEWS_TOP_SECTION_TEST_ID}>
            <WfHeading wordBreak="break-word" as="h1" fontSize={["3xl", "3xl", "4xl"]} fontWeight={["bold", "bold", "heavy"]} mb="3">
                {headline}
            </WfHeading>
            <Flex gap={[1, 1, 1]} mb={2} color="gray.400" fontSize="lg" direction={["column", "row"]} align={["flex-start", "center"]}>
                <IconDpaAfx h="18px" width="auto" mr="1" />

                <Flex direction="row" fontSize={["md", "lg"]}>
                    {creationDate && (
                        <Flex gap="1" color="gray.400" align="center">
                            <Text>{formatDateShort(creationDate)}</Text>
                            <Text color="gray.800">&bull;</Text>
                            <Text>{formatTimeShort(creationDate)}</Text>
                        </Flex>
                    )}
                    {location && creationDate ? (
                        <Text mx={1} color="gray.800">
                            &bull;
                        </Text>
                    ) : null}
                    {location && (
                        <Flex gap="1" color="gray.400" noOfLines={1} wordBreak="break-all" align="center">
                            <Text>{location}</Text>
                        </Flex>
                    )}
                </Flex>
            </Flex>
            {tags.length > 0 && <ExpandableTags mb={4} tags={tags} />}
            <VStack align="start" gap={1} mb={3}>
                <Flex alignItems="center" data-test-id={NEWS_COMMUNITY_REACTIONS_TEST_ID}>
                    <IconTrending boxSize={3} alignSelf="normal" />
                    <Text pl={0.5} overflow="hidden" whiteSpace="nowrap" fontWeight="bold" fontSize={["md", "lg"]}>
                        {t("top-section.community-reactions", { count: totalInteractions })}
                    </Text>
                </Flex>
                <HStack spacing={2} fontSize={["md", "lg"]} flexWrap="wrap" rowGap={1}>
                    <WfLink href={`#${reactionSectionId}`} fontWeight="normal">
                        {t("top-section.reactions", { count: numberOfReactions })}
                    </WfLink>
                    <WfLink href={`#${commentSectionId}`} fontWeight="normal">
                        {t("top-section.comments", { count: numberOfComments })}
                    </WfLink>
                    <WfLink href={`#${likeSectionId}`} fontWeight="normal">
                        {t("top-section.likes", { count: numberOfLikes })}
                    </WfLink>
                </HStack>
            </VStack>
        </Box>
    );
};
